/**
 * p5ToInstance — convert a global-mode p5.js sketch (as authored in the editor)
 * into an *instance-mode factory* that the AR viewer's shared-p5 bridge can run.
 *
 * Output contract:
 *   convertSketch(source) => {
 *     factorySource: string,                 // an expression: (p) => { ... }
 *     metadata: {
 *       width, height, webgl,                // captured from createCanvas(...)
 *       usesDegrees                          // the sketch called angleMode(DEGREES)
 *     }
 *   }
 *
 * The factory attaches p.setup / p.draw / ... onto a supplied p5 instance — the
 * same shape as the bridge's hand-written sketches (e.g. leoSketch(p)). It never
 * calls `new p5(...)`; the bridge owns instantiation and the canvas.
 *
 * Design notes:
 *  - Scope-aware: an identifier is prefixed with `p.` only when it is a *free
 *    reference* (no user binding in scope) whose name is a real p5 global. This is
 *    what makes name-clashes (`let color`) and local shadowing (`let height`) safe.
 *  - The p5 name set is the union of the editor's own keyword lists, so it stays in
 *    sync with the rest of the editor.
 *  - The bridge runs in RADIANS, so `angleMode(DEGREES)` is STRIPPED — it would corrupt
 *    the bridge's own transform maths. The converter does NOT convert the sketch's angle
 *    values to compensate; it reports `metadata.usesDegrees` instead, and the editor shows
 *    the student a warning telling them to wrap angles in `radians(...)`.
 *
 *    Why report rather than convert: converting was only ever partial. Inverse trig
 *    (asin/acos/atan/atan2) RETURNS an angle, and those outputs were never converted — so a
 *    degrees sketch came out half-right, in AR only, with nothing saying so. A warning the
 *    student can act on beats a silent, incomplete rewrite.
 */

import { parse } from '@babel/parser';
import traverseModule from '@babel/traverse';
import generatorModule from '@babel/generator';
import * as t from '@babel/types';
import { p5FunctionKeywords, p5VariableKeywords } from './p5-keywords';

const traverse = traverseModule.default || traverseModule;
const generate = generatorModule.default || generatorModule;

// Names that, when referenced freely, should be rewritten to `p.<name>`.
const P5_NAMES = new Set([
  ...Object.keys(p5FunctionKeywords),
  ...Object.keys(p5VariableKeywords)
]);
// `p5` is the global constructor (e.g. `new p5.Vector()`); never prefix it.
P5_NAMES.delete('p5');

// Functions p5 auto-binds from the global scope in global mode. Top-level
// definitions of these become `p.<name> = ...` on the instance.
const LIFECYCLE = new Set([
  'setup',
  'draw',
  'preload',
  'windowResized',
  'mousePressed',
  'mouseReleased',
  'mouseMoved',
  'mouseDragged',
  'mouseClicked',
  'doubleClicked',
  'mouseWheel',
  'keyPressed',
  'keyReleased',
  'keyTyped',
  'touchStarted',
  'touchMoved',
  'touchEnded',
  'deviceMoved',
  'deviceTurned',
  'deviceShaken'
]);

// p5.strands entry points that take a shader-DSL callback as their first argument.
// (The `load*Shader(url, ...)` variants fetch their callback from a separate file,
// so the converter never sees that source and has nothing to protect.)
const STRANDS_BUILDERS = new Set([
  'buildColorShader',
  'buildMaterialShader',
  'buildStrokeShader',
  'buildNormalShader',
  'buildFilterShader',
  'buildComputeShader'
]);

/**
 * If this call is a p5.strands entry point, return the DSL callback node it was
 * handed; otherwise null.
 *
 * Why this matters: p5 does not merely *call* a strands callback. It takes
 * `cb.toString()`, re-parses it, transpiles the strands DSL, and recompiles the body
 * with `new Function('__p5', ...paramNames, body)` (`buildStrandsCallback`, p5 2.x).
 * A `new Function` body is compiled in *global* scope, so it cannot see the enclosing
 * closure — and our instance identifier is a parameter of the generated factory.
 * Prefixing anything in there yields `ReferenceError: <ident> is not defined` the
 * moment the shader is built. Left alone the names resolve fine, because p5 binds its
 * whole prototype onto `window` for the duration of the callback (`withTempGlobalMode`).
 *
 * Heuristic note: any `.modify(fn)` is treated as strands. `modify` is *the* documented
 * p5.strands idiom, and the failure modes are symmetric (rewriting a real strands
 * callback breaks it just as surely as skipping a non-strands one), so we favour the
 * overwhelmingly more likely reading.
 */
function strandsCallbackOf(path) {
  const { callee } = path.node;
  const arg = path.node.arguments[0];
  if (!arg) return null;
  if (!t.isFunctionExpression(arg) && !t.isArrowFunctionExpression(arg))
    return null;

  if (callee.type === 'Identifier') {
    // A user-defined function of the same name shadows the p5 builder.
    if (path.scope.getBinding(callee.name)) return null;
    return STRANDS_BUILDERS.has(callee.name) ? arg : null;
  }
  if (
    t.isMemberExpression(callee) &&
    !callee.computed &&
    t.isIdentifier(callee.property)
  ) {
    const prop = callee.property.name;
    return prop === 'modify' || STRANDS_BUILDERS.has(prop) ? arg : null;
  }
  return null;
}

const PARSE_OPTIONS = {
  sourceType: 'script',
  allowReturnOutsideFunction: true
};

function literalNumber(node) {
  if (node && node.type === 'NumericLiteral') return node.value;
  return null;
}

function isWebglArg(node) {
  return (
    !!node &&
    node.type === 'Identifier' &&
    (node.name === 'WEBGL' || node.name === 'WEBGL2')
  );
}

// Name of the p5 instance the factory receives, used as the prefix for every free
// p5 reference (`fill()` -> `<INSTANCE_IDENT>.fill()`). Deliberately NOT `p`: the
// sketch is authored by students who may well have a variable named `p` (or `q`,
// `_p`, …), and that would shadow the factory parameter and break every p5 call. This
// output is machine-eval'd, never read by a human, so it can be an intentionally
// bizarre sentinel that no hand-written sketch would ever collide with.
const INSTANCE_IDENT = '$__p5$AR$instance__$';

function memberOnP(name) {
  return t.memberExpression(t.identifier(INSTANCE_IDENT), t.identifier(name));
}

/**
 * @param {string} source global-mode p5 sketch source
 * @param {object} [options]
 * @param {boolean} [options.stripOrbitControl=false] remove `orbitControl(...)` calls.
 *   orbitControl drives the p5 camera from mouse/touch, which fights the AR bridge that
 *   owns the camera; the AR publish path passes this true (see AR_SETTINGS_DEFAULTS).
 * @param {boolean} [options.stripBackground=false] remove `background(...)` calls.
 *   background paints an opaque fill over the whole canvas, hiding the AR camera feed
 *   behind the overlay; the AR publish path passes this true (see AR_SETTINGS_DEFAULTS).
 * @returns {{ factorySource: string, metadata: { width: number|null, height: number|null, webgl: boolean, usesDegrees: boolean } }}
 */
function convertSketch(source, options = {}) {
  const { stripOrbitControl = false, stripBackground = false } = options;
  const ast = parse(source, PARSE_OPTIONS);

  // --- Pass 1: gather facts without mutating ---
  const metadata = {
    width: null,
    height: null,
    webgl: false,
    usesDegrees: false
  };
  // Callback nodes p5.strands will recompile with `new Function`; Pass 2 must leave
  // everything inside them verbatim. Collected here, before any rewriting, so the
  // detection can't be confused by Pass 2's own edits to the callee.
  const strandsCallbacks = new Set();

  traverse(ast, {
    CallExpression(path) {
      const strandsCallback = strandsCallbackOf(path);
      if (strandsCallback) strandsCallbacks.add(strandsCallback);

      const { callee } = path.node;
      if (callee.type !== 'Identifier') return;
      if (path.scope.getBinding(callee.name)) return; // user-defined, not p5

      if (callee.name === 'angleMode') {
        const arg = path.node.arguments[0];
        if (arg && arg.type === 'Identifier' && arg.name === 'DEGREES') {
          metadata.usesDegrees = true;
        }
      }

      if (callee.name === 'createCanvas') {
        const [w, h, mode] = path.node.arguments;
        metadata.width = literalNumber(w);
        metadata.height = literalNumber(h);
        metadata.webgl = isWebglArg(mode);
      }
    }
  });

  // --- Pass 2: rewrite ---
  traverse(ast, {
    // p5.strands DSL callbacks are recompiled by p5 in global scope, where neither the
    // factory parameter nor any injected local exists. Rewrite *nothing* inside them.
    'FunctionExpression|ArrowFunctionExpression': (path) => {
      if (strandsCallbacks.has(path.node)) path.skip();
    },

    // `function draw() {}` at the top level -> `p.draw = function () {}`
    FunctionDeclaration(path) {
      if (!path.parentPath.isProgram()) return;
      const { id } = path.node;
      if (!id || !LIFECYCLE.has(id.name)) return;
      path.replaceWith(
        t.expressionStatement(
          t.assignmentExpression(
            '=',
            memberOnP(id.name),
            // Preserve async/generator flags — an `async function setup()` that
            // uses `await` becomes a syntax error if we drop them.
            t.functionExpression(
              null,
              path.node.params,
              path.node.body,
              path.node.generator,
              path.node.async
            )
          )
        )
      );
    },

    // `const draw = () => {}` / `var draw = function () {}` at the top level
    VariableDeclaration(path) {
      if (!path.parentPath.isProgram()) return;
      if (path.node.declarations.length !== 1) return;
      const decl = path.node.declarations[0];
      if (decl.id.type !== 'Identifier' || !LIFECYCLE.has(decl.id.name)) return;
      if (
        !decl.init ||
        !(
          t.isFunctionExpression(decl.init) ||
          t.isArrowFunctionExpression(decl.init)
        )
      ) {
        return;
      }
      path.replaceWith(
        t.expressionStatement(
          t.assignmentExpression('=', memberOnP(decl.id.name), decl.init)
        )
      );
    },

    // `draw = function () {}` (implicit global assignment) -> `p.draw = ...`
    AssignmentExpression(path) {
      const { left } = path.node;
      if (
        left.type === 'Identifier' &&
        LIFECYCLE.has(left.name) &&
        !path.scope.getBinding(left.name)
      ) {
        path.get('left').replaceWith(memberOnP(left.name));
      }
    },

    CallExpression(path) {
      const { callee } = path.node;
      if (callee.type !== 'Identifier') return;
      const { name } = callee;
      if (path.scope.getBinding(name)) return; // user-defined function

      // The bridge owns the canvas: drop createCanvas (dims already captured).
      if (name === 'createCanvas') {
        path.replaceWith(t.identifier('undefined'));
        return;
      }

      // The bridge owns the camera: drop orbitControl so it can't fight it (opt-in).
      if (stripOrbitControl && name === 'orbitControl') {
        path.replaceWith(t.identifier('undefined'));
        return;
      }

      // Keep the AR overlay transparent: drop background so it can't paint over the
      // camera feed (opt-in).
      if (stripBackground && name === 'background') {
        path.replaceWith(t.identifier('undefined'));
        return;
      }

      // Keep the instance in radians: drop angleMode(DEGREES). The sketch's angle VALUES
      // are deliberately left alone — metadata.usesDegrees reports the problem instead,
      // and the editor tells the student to wrap them in radians(...). See the header.
      if (name === 'angleMode') {
        const arg = path.node.arguments[0];
        if (arg && arg.type === 'Identifier' && arg.name === 'DEGREES') {
          path.replaceWith(t.identifier('undefined'));
        }
      }
    },

    // The core: prefix free references to p5 globals.
    Identifier(path) {
      const { name } = path.node;
      if (!P5_NAMES.has(name)) return;
      if (!path.isReferencedIdentifier()) return; // skip keys, props, decl ids
      if (path.scope.getBinding(name)) return; // user binding shadows the p5 name
      path.replaceWith(memberOnP(name));
      path.skip();
    }
  });

  // --- Wrap the program body into the instance factory ---
  const factory = t.arrowFunctionExpression(
    [t.identifier(INSTANCE_IDENT)],
    t.blockStatement(ast.program.body)
  );

  const factorySource = generate(factory).code;

  return { factorySource, metadata };
}

export { convertSketch, INSTANCE_IDENT };
